/**
 * Centralised DOMPurify sanitisation configuration.
 *
 * All components that render user or LLM-generated content via
 * dangerouslySetInnerHTML MUST use this config to ensure consistent
 * XSS protection across the application.
 *
 * @see PWI-194 — REM-05: Lack of Input and Output Encoding
 */
import type { Config } from "dompurify";

/**
 * Tags allowed in sanitised markdown/HTML output.
 * Covers standard markdown elements only — no interactive or embedded content.
 */
export const SANITISE_ALLOWED_TAGS: readonly string[] = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "p",
  "br",
  "hr",
  "strong",
  "em",
  "del",
  "s",
  "code",
  "pre",
  "ul",
  "ol",
  "li",
  "a",
  "img",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
  "blockquote",
];

/**
 * Attributes allowed on sanitised elements.
 *
 * NOTE: "style" is deliberately excluded to prevent CSS-based attacks
 * (e.g., data exfiltration via background-image url(), UI redress via
 * position:fixed). The "align" attribute is kept for table cell alignment.
 */
export const SANITISE_ALLOWED_ATTR: readonly string[] = ["href", "src", "alt", "title", "align"];

/**
 * Tags explicitly forbidden as belt-and-suspenders defence.
 * These are blocked even if a future change accidentally adds them to ALLOWED_TAGS.
 */
export const SANITISE_FORBID_TAGS: readonly string[] = [
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "form",
  "input",
  "textarea",
  "select",
  "svg",
  "math",
];

/**
 * Event handler attributes explicitly forbidden as belt-and-suspenders.
 * DOMPurify already strips all attributes not in ALLOWED_ATTR — this list
 * makes the policy explicit for the most common XSS attack vectors.
 * It is intentionally non-exhaustive (there are 100+ on* attributes).
 */
export const SANITISE_FORBID_ATTR: readonly string[] = [
  "onerror",
  "onload",
  "onclick",
  "onmouseover",
  "onfocus",
  "onblur",
  "onsubmit",
  "onchange",
];

/**
 * Shared DOMPurify configuration for sanitising markdown/HTML content.
 * Used by all components that render user or LLM-generated HTML via dangerouslySetInnerHTML.
 */
export const SANITISE_CONFIG: Config = {
  ALLOWED_TAGS: [...SANITISE_ALLOWED_TAGS],
  ALLOWED_ATTR: [...SANITISE_ALLOWED_ATTR],
  FORBID_TAGS: [...SANITISE_FORBID_TAGS],
  FORBID_ATTR: [...SANITISE_FORBID_ATTR],
  ALLOW_DATA_ATTR: false,
};
